// Types matching the backend API

export interface User {
  id: string;
  email: string;
  name: string;
  creditsBalance: number;
  stripeCustomerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  userId: string;
  name: string;
  description?: string;
  context?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FileRecord {
  id: string;
  projectId: string;
  userId: string;
  filename: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  s3Key: string;
  processingStatus: 'unprocessed' | 'processing' | 'completed' | 'failed';
  pageCount?: number;
  processedAt?: string;
  errorMessage?: string;
  createdAt: string;
}

export interface CreditTransaction {
  id: string;
  userId: string;
  amount: number;
  type: 'purchase' | 'usage' | 'bonus';
  description: string;
  stripePaymentIntentId?: string;
  createdAt: string;
}

export interface ChatSession {
  id: string;
  projectId: string;
  userId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  userId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

// API Response types
export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends APIResponse<T[]> {
  pagination?: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

// Auth types
export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  email_verified: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

export interface ConfirmEmailRequest {
  email: string;
  confirmationCode: string;
  password: string;
}

export interface LoginResponse extends APIResponse<{
  accessToken: string;
  idToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    creditsBalance: number;
  };
}> {}

// Credit types
export interface CreditPurchaseRequest {
  credits: number; // Number of credits to purchase
  currency: 'USD' | 'AUD'; // Currency for payment
}

export interface CreditPurchaseResponse extends APIResponse<{
  clientSecret: string;
  amount: number; // Total cost in the selected currency
  credits: number;
  currency: string;
}> {}

export interface CreditPricing {
  USD: {
    currency: 'USD';
    pricePerCredit: number;
    symbol: string;
  };
  AUD: {
    currency: 'AUD';
    pricePerCredit: number;
    symbol: string;
  };
}

export interface CreditsResponse extends APIResponse<{
  balance: number;
  transactions: CreditTransaction[];
  pricing: CreditPricing;
}> {}

// Project types
export interface CreateProjectRequest {
  name: string;
  description?: string;
  context?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  context?: string;
}

export interface ProjectsResponse extends APIResponse<{
  projects: Project[];
  total: number;
}> {}

// File types
export interface FileUploadRequest {
  filename: string;
  mimeType: string;
  fileSize: number;
}

export interface FileUploadResponse extends APIResponse<{
  file: FileRecord;
  uploadUrl: string;
  downloadUrl: string;
  autoProcess?: boolean;
}> {}

export interface FileProcessResponse extends APIResponse<{
  file: FileRecord;
  creditsUsed: number;
  remainingCredits: number;
}> {}

export interface ProjectFilesResponse extends APIResponse<{
  files: FileRecord[];
  total: number;
}> {}

export interface FileDownloadResponse extends APIResponse<{
  file: FileRecord;
  downloadUrl: string;
}> {}

export interface ProcessingStatusResponse extends APIResponse<{
  status: 'unprocessed' | 'processing' | 'completed' | 'failed';
  currentStep?: string;
  processedAt?: string;
  llmJobId?: string;
}> {}

export interface LLMResultsResponse extends APIResponse<{
  results: {
    clusteredData?: string; // presigned URL
    reportPdf?: string; // presigned URL
    reportJson?: string; // presigned URL
  };
}> {}

// Error types
export class APIError extends Error {
  constructor(
    public message: string,
    public statusCode: number = 500,
    public code?: string
  ) {
    super(message);
    this.name = 'APIError';
  }
}

export class ValidationError extends APIError {
  constructor(message: string) {
    super(message, 400, 'VALIDATION_ERROR');
  }
}

export class UnauthorizedError extends APIError {
  constructor(message: string = 'Unauthorized') {
    super(message, 401, 'UNAUTHORIZED');
  }
}

export class ForbiddenError extends APIError {
  constructor(message: string = 'Forbidden') {
    super(message, 403, 'FORBIDDEN');
  }
}

export class NotFoundError extends APIError {
  constructor(message: string = 'Not found') {
    super(message, 404, 'NOT_FOUND');
  }
}

export class InsufficientCreditsError extends APIError {
  constructor(message: string = 'Insufficient credits') {
    super(message, 402, 'INSUFFICIENT_CREDITS');
  }
}